import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  inviteUser,
  listAllowlistUsers,
  removeUser,
  setUserRole,
  type AllowlistUser,
} from "../../lib/api";
import type { AllowlistRole } from "../../lib/auth";
import { useAuth } from "../../lib/auth";

const ROLE_OPTIONS: { label: string; value: AllowlistRole }[] = [
  { label: "Admin", value: "admin" },
  { label: "User", value: "user" },
  { label: "Executive (read-only)", value: "executive" },
];

function formatDate(value?: string): string {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : value;
}

export default function UsersTab() {
  const { message } = App.useApp();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AllowlistUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingEmail, setSavingEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState<AllowlistRole>("user");
  const [adding, setAdding] = useState(false);
  const [lastActionLink, setLastActionLink] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listAllowlistUsers();
      setUsers(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const adminCount = useMemo(() => users.filter((u) => u.role === "admin").length, [users]);

  const onChangeRole = async (email: string, role: AllowlistRole) => {
    // Mirror the backend "last admin" guard so the UI gives an immediate hint.
    const target = users.find((u) => u.email === email);
    if (target?.role === "admin" && role !== "admin" && adminCount <= 1) {
      message.warning("Cannot demote the last remaining admin.");
      return;
    }
    setSavingEmail(email);
    try {
      await setUserRole(email, role);
      message.success(`Updated ${email} to ${role}`);
      await refresh();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingEmail(null);
    }
  };

  const onRemove = async (email: string) => {
    const target = users.find((u) => u.email === email);
    if (target?.role === "admin" && adminCount <= 1) {
      message.warning("Cannot remove the last remaining admin.");
      return;
    }
    try {
      await removeUser(email);
      message.success(`Removed ${email}`);
      await refresh();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err));
    }
  };

  const onAdd = async () => {
    const email = addEmail.trim().toLowerCase();
    if (!email) {
      message.warning("Email is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      message.warning("Enter a valid email address.");
      return;
    }
    setAdding(true);
    setLastActionLink(null);
    try {
      const result = await inviteUser(email, addRole);
      message.success(`Invite sent to ${email}`);
      setLastActionLink(result.actionLink);
      setAddEmail("");
      setAddRole("user");
      await refresh();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err));
    } finally {
      setAdding(false);
    }
  };

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email: string) => (
        <Typography.Text style={{ fontFamily: "monospace" }}>{email}</Typography.Text>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 200,
      render: (role: AllowlistRole, row: AllowlistUser) => (
        <Select<AllowlistRole>
          value={role}
          options={ROLE_OPTIONS}
          onChange={(value) => onChangeRole(row.email, value)}
          loading={savingEmail === row.email}
          disabled={savingEmail === row.email}
          style={{ width: 180 }}
        />
      ),
    },
    {
      title: "Added by",
      dataIndex: "addedBy",
      key: "addedBy",
      render: (v?: string) => v || "—",
    },
    {
      title: "Added at",
      dataIndex: "addedAt",
      key: "addedAt",
      render: (v?: string) => formatDate(v),
    },
    {
      title: "Last modified by",
      dataIndex: "lastModifiedBy",
      key: "lastModifiedBy",
      render: (v?: string) => v || "—",
    },
    {
      title: "Last modified at",
      dataIndex: "lastModifiedAt",
      key: "lastModifiedAt",
      render: (v?: string) => formatDate(v),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: unknown, row: AllowlistUser) => {
        const isSelf = currentUser?.email?.toLowerCase() === row.email;
        const isLastAdmin = row.role === "admin" && adminCount <= 1;
        const disabled = isSelf || isLastAdmin;
        const reason = isSelf
          ? "You can't remove your own account."
          : isLastAdmin
            ? "Cannot remove the last remaining admin."
            : undefined;
        return (
          <Popconfirm
            title={`Remove ${row.email} from the allowlist?`}
            description="They will lose access on their next request."
            okText="Remove"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => onRemove(row.email)}
            disabled={disabled}
          >
            <Button
              danger
              size="small"
              disabled={disabled}
              title={reason}
            >
              Remove
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="Invite user">
        <Form layout="vertical" component="div" onFinish={onAdd}>
          <Row gutter={12} align="bottom">
            <Col xs={24} md={12}>
              <Form.Item label="Email" htmlFor="user-add-email" style={{ marginBottom: 0 }}>
                <Input
                  id="user-add-email"
                  type="email"
                  placeholder="user@nc.gov"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  onPressEnter={onAdd}
                  autoComplete="off"
                />
              </Form.Item>
            </Col>
            <Col xs={16} md={8}>
              <Form.Item label="Role" htmlFor="user-add-role" style={{ marginBottom: 0 }}>
                <Select
                  id="user-add-role"
                  value={addRole}
                  options={ROLE_OPTIONS}
                  onChange={(v) => setAddRole(v)}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={8} md={4}>
              <Button
                type="primary"
                onClick={onAdd}
                loading={adding}
                block
              >
                Invite
              </Button>
            </Col>
          </Row>
        </Form>
        {lastActionLink && (
          <Alert
            style={{ marginTop: 16 }}
            type="info"
            showIcon
            message="If the invite email doesn't arrive, share this link directly:"
            description={
              <Typography.Paragraph
                copyable={{ text: lastActionLink }}
                style={{ marginBottom: 0, wordBreak: "break-all" }}
              >
                {lastActionLink}
              </Typography.Paragraph>
            }
          />
        )}
      </Card>

      {error && (
        <Card>
          <Typography.Text type="danger">Failed to load allowlist: {error}</Typography.Text>
        </Card>
      )}

      <Card
        title={`Allowlisted users (${users.length})`}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={refresh}
            loading={loading}
            size="small"
          >
            Refresh
          </Button>
        }
      >
        <Table<AllowlistUser>
          rowKey="email"
          dataSource={users}
          columns={columns}
          loading={loading}
          pagination={false}
          locale={{ emptyText: "No users in the allowlist yet." }}
        />
      </Card>
    </Space>
  );
}
